import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ModalBasicLayout from "./ModalBasicLayout";
import IconButton from "../Buttons/IconButton";
import { ConfirmAlert } from "../Alerts/Alerts";
import { useDispatchApp } from "../../lib/redux";
import { useTranslationApp } from "../../lib/i18next";
import {
  startGetCustomReportPrompts,
  startRemoveCustomReportPrompt,
} from "../../actions/customReportPrompts";
import {
  BUTTON_GO_BACK,
  MODAL_CUSTOM_REPORT_SAVED_PROMPTS_DELETE,
  MODAL_CUSTOM_REPORT_SAVED_PROMPTS_DELETE_DESCRIPTION,
  MODAL_CUSTOM_REPORT_SAVED_PROMPTS_DELETE_TITLE,
  MODAL_CUSTOM_REPORT_SAVED_PROMPTS_DESCRIPTION,
  MODAL_CUSTOM_REPORT_SAVED_PROMPTS_EMPTY,
  MODAL_CUSTOM_REPORT_SAVED_PROMPTS_TITLE,
} from "../../i18n/keysTranslations";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import { BORDER_RADIUS } from "../../utils/constants";
import ContainerModalActions from "../Containers/ContainerModalActions";
import DialogActions from "@mui/material/DialogActions";
import Loader from "../Loaders/Loader";

const ModalCustomReportSavedPrompts = ({
  modalOpen = false,
  onCloseModal = () => {},
  onSelectPrompt = () => {},
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const [prompts, setPrompts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!modalOpen) {
      return;
    }

    let cancelled = false;
    const loadPrompts = async () => {
      setIsLoading(true);
      const response = await dispatch(startGetCustomReportPrompts());
      if (cancelled) {
        return;
      }
      setPrompts(response?.ok ? response.data : []);
      setIsLoading(false);
    };

    loadPrompts();
    return () => {
      cancelled = true;
    };
  }, [modalOpen, dispatch]);

  const onDeletePrompt = async (event, item) => {
    event.stopPropagation();
    const result = await ConfirmAlert({
      title: t(MODAL_CUSTOM_REPORT_SAVED_PROMPTS_DELETE_TITLE),
      text: t(MODAL_CUSTOM_REPORT_SAVED_PROMPTS_DELETE_DESCRIPTION),
    });
    if (!result?.isConfirmed) {
      return;
    }

    const response = await dispatch(
      startRemoveCustomReportPrompt({ promptHash: item.id })
    );
    if (response?.ok) {
      setPrompts((prev) => prev.filter((prompt) => prompt.id !== item.id));
    }
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      title={t(MODAL_CUSTOM_REPORT_SAVED_PROMPTS_TITLE)}
      description={t(MODAL_CUSTOM_REPORT_SAVED_PROMPTS_DESCRIPTION)}
      maxWidth="sm"
      fullWidth
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          width: "100%",
          minHeight: 120,
        }}
      >
        {isLoading ? (
          <Loader size={24} hasMarginTop />
        ) : prompts.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t(MODAL_CUSTOM_REPORT_SAVED_PROMPTS_EMPTY)}
          </Typography>
        ) : (
          prompts.map((item) => (
            <Box
              key={item.id}
              onClick={() => onSelectPrompt(item.prompt)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1.5,
                cursor: "pointer",
                borderRadius: `${BORDER_RADIUS}px`,
                border: `1px solid ${INPUT_COLOR_BORDER}`,
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {item.prompt}
              </Typography>
              <IconButton
                edge={false}
                tooltipText={MODAL_CUSTOM_REPORT_SAVED_PROMPTS_DELETE}
                onClick={(event) => onDeletePrompt(event, item)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))
        )}
      </Box>
      <DialogActions sx={{ p: 0 }}>
        <ContainerModalActions
          hasPadding={true}
          propsSecondaryButton={{
            showButton: true,
            text: BUTTON_GO_BACK,
            onClick: onCloseModal,
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

export default ModalCustomReportSavedPrompts;
